"use client";

import type {
  PersistedClient,
  Persister,
} from "@tanstack/react-query-persist-client";
import { del, get, set } from "idb-keyval";

const IDB_KEY = "hoai-react-query-cache";

export function createIdbPersister(): Persister {
  return {
    persistClient: async (client: PersistedClient) => {
      try {
        await set(IDB_KEY, client);
      } catch {
        // IndexedDB unavailable (private browsing, quota exceeded, etc.)
      }
    },
    restoreClient: async () => {
      try {
        return await get<PersistedClient>(IDB_KEY);
      } catch {
        return undefined;
      }
    },
    // Same delete as the standalone wipe below — one implementation, so the
    // buster/maxAge path and the "this state is wrong" paths cannot drift.
    removeClient: wipePersistedCache,
  };
}

/**
 * H2-2125 — delete the persisted cache blob outright, without going through a
 * `Persister` instance.
 *
 * `PersistQueryClientProvider` owns the only persister the app constructs, and
 * it only calls `removeClient()` on a buster mismatch or a `maxAge` expiry.
 * Every other "this state is wrong, throw it away" path — sign-out, an
 * involuntary 401, an org switch, a detected desync — could reach the
 * in-memory `QueryClient` but not the disk copy, so the corruption came
 * straight back on the next load.
 *
 * Worse, `QueryClient.clear()` alone is not enough before a navigation: the
 * write-back to IndexedDB is a debounced subscription inside
 * `persistQueryClientSubscribe`, and `window.location.reload()` routinely
 * outruns it. Awaiting THIS is what makes the wipe actually stick.
 *
 * Safe to call anywhere: no-ops on the server and swallows a hostile
 * IndexedDB (private browsing, quota, Safari's ITP eviction).
 */
export async function wipePersistedCache(): Promise<void> {
  if (typeof window === "undefined") return;
  try {
    await del(IDB_KEY);
  } catch {
    // Ignore — an unreachable IndexedDB has nothing to leak back either.
  }
}
